import { useState } from 'react'
import {
  Grid,
  Box,
  TextField,
  Button,
  Tooltip,
  Dialog,
  DialogContent,
  IconButton,
} from '@mui/material'
import { Search } from 'lucide-react'
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import ConfirmDialog from '../dialogs/ConfirmDialog'

type SearchableDataTableProps<T> = {
  label: string
  placeholder?: string
  columns: GridColDef[]
  rows: T[]
  totalCount: number
  loading: boolean
  onSearch: (search: string) => void
  onPageChange: (page: number) => void
  AddFormComponent: (props: { onClose: () => void }) => React.ReactNode
  enableRowActions?: boolean
  isRowSelected?: (row: T) => boolean
  onToggleRow?: (row: T) => void
  showConfirmDialog?: (row: T) => boolean
  confirmDialogText?: string
}

export const SearchableDataTable = <T extends unknown>({
  label,
  placeholder = '',
  columns,
  rows,
  totalCount,
  loading,
  onSearch,
  onPageChange,
  AddFormComponent,
  enableRowActions = false,
  isRowSelected,
  onToggleRow,
  showConfirmDialog,
  confirmDialogText
}: SearchableDataTableProps<T>) => {
  const [openAddModal, setOpenAddModal] = useState(false)
  const [rowToConfirm, setRowToConfirm] = useState<T | null>(null)

  const handleToggleClick = (row: T, selected: boolean) => {
    if (showConfirmDialog?.(row) && !selected) {
      setRowToConfirm(row)
      return
    }

    onToggleRow?.(row)
  }

  const finalColumns: GridColDef[] = enableRowActions
    ? [
        {
          field: 'actions',
          headerName: '',
          width: 60,
          sortable: false,
          filterable: false,
          renderCell: ({ row }: { row: T }) => {
            const selected = isRowSelected?.(row) ?? false
            return (
              <Tooltip
                title={selected ? 'Retirer de la sélection' : 'Ajouter à la sélection'}
                arrow
              >
                <IconButton onClick={() => handleToggleClick(row, selected)}>
                  {selected ? (
                    <CheckBoxIcon color="primary" />
                  ) : (
                    <CheckBoxOutlineBlankIcon />
                  )}
                </IconButton>
              </Tooltip>
            )
          },
        },
        ...columns,
      ]
    : columns

  return (
    <Grid container item xs={12} direction="column" sx={{ height: '100%', flex: 1 }} data-testid="searchable-data-table">
      <Box display="flex" alignItems="center" className="mb-2">
        <TextField
          label={label}
          placeholder={placeholder}
          onChange={(e) => onSearch(e.target.value)}
          InputProps={{ endAdornment: <Search size={20} className="text-gray-500" /> }}
          className="w-full rounded-xl bg-gray-50"
        />

        <span className="inline-flex items-center ml-2">
          <Tooltip title="Ajouter" arrow>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => setOpenAddModal(true)}
              sx={{
                height: 55,
                paddingLeft: 1.5,
                paddingRight: 1.5,
                minWidth: 'auto',
                borderColor: '#e5e7eb',
              }}
            >
              <i className="fas fa-plus" />
            </Button>
          </Tooltip>
        </span>
      </Box>

      <DataGridPro
        columns={finalColumns}
        rows={rows}
        rowCount={totalCount}
        loading={loading}
        density="standard"
        headerHeight={26}
        rowHeight={36}
        pagination
        paginationMode="server"
        pageSize={10}
        onPageChange={onPageChange}
        disableSelectionOnClick
        disableMultipleSelection
        style={{
          minHeight: 300,
          height: '48.7vh',
          backgroundColor: 'white',
          borderRadius: '15px 15px 25px 25px',
        }}
        sx={{
          '& .MuiDataGrid-row:nth-of-type(even)': {
            backgroundColor: '#f8f8f8',
          },
        }}
      />

      <Dialog open={openAddModal} onClose={() => setOpenAddModal(false)} maxWidth="md" fullWidth>
        <DialogContent>
          {AddFormComponent({ onClose: () => setOpenAddModal(false) })}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={Boolean(rowToConfirm)}
        title="Confirmation"
        message={confirmDialogText ?? 'Souhaitez-vous ajouter cet élément à la sélection ?'}
        onConfirm={() => {
          if (rowToConfirm) {
            onToggleRow?.(rowToConfirm)
          }
          setRowToConfirm(null)
        }}
        onCancel={() => setRowToConfirm(null)}
      />
    </Grid>
  )
}

export default SearchableDataTable
